<template>
 <a-row v-if="roiInput" class="mb-5">
  <a-col :xs="24" class="mb-2">
   <label>Terugverdienberekening</label>
  </a-col>
  <a-col :xs="24" class="mb-2">
   <small>Voor de terugverdientijd per product in de offerte. Leeg laten mag; dan rekenen we met een standaardwaarde.
    Gasverbruik en tarieven nemen we over van hierboven.</small>
  </a-col>
  <a-col :xs="24">
   <a-row :gutter="30">
    <template v-if="needs.needsIsolation">
     <a-col :xs="24" class="mb-1"><strong>Isolatie *</strong> <small>verplicht: vul de m² in van minimaal één maatregel</small></a-col>
     <a-col :md="6" :xs="24" class="mb-2">
      <a-input v-model:value="roiInput.isolatie_spouw_m2" addon-after="m²" addon-before="Spouw" type="number"/>
     </a-col>
     <a-col :md="6" :xs="24" class="mb-2">
      <a-input v-model:value="roiInput.isolatie_vloer_m2" addon-after="m²" addon-before="Vloer" type="number"/>
     </a-col>
     <a-col :md="6" :xs="24" class="mb-2">
      <a-input v-model:value="roiInput.isolatie_bodem_m2" addon-after="m²" addon-before="Bodem" type="number"/>
     </a-col>
     <a-col :md="6" :xs="24" class="mb-2">
      <a-input v-model:value="roiInput.isolatie_dak_m2" addon-after="m²" addon-before="Dak" type="number"/>
     </a-col>
     <a-col :xs="24" class="mb-2">
      <a-form ref="isolationForm" :model="isolationModel" :rules="isolationRules">
       <a-form-item class="mb-0" name="totalM2"/>
      </a-form>
     </a-col>
    </template>

    <template v-if="needs.needsHeatPump">
     <a-col :xs="24" class="mb-1"><strong>Warmtepomp</strong></a-col>
     <a-col :md="12" :xs="24" class="mb-2">
      <a-form-item label="Afgiftesysteem">
       <a-radio-group v-model:value="roiInput.wp_afgiftesysteem" button-style="solid">
        <a-radio-button value="radiator">Bestaande radiatoren</a-radio-button>
        <a-radio-button value="lt">LT-radiatoren</a-radio-button>
        <a-radio-button value="vloer">Vloerverwarming</a-radio-button>
       </a-radio-group>
      </a-form-item>
     </a-col>
    </template>

    <template v-if="needs.needsSolar">
     <a-col :xs="24" class="mb-1"><strong>Zonnepanelen</strong></a-col>
     <a-col :md="12" :xs="24" class="mb-2">
      <a-input v-model:value="roiInput.zon_kwp" addon-after="kWp" addon-before="Vermogen" type="number"/>
     </a-col>
     <a-col :md="12" :xs="24" class="mb-2">
      <a-form-item label="Oriëntatie">
       <a-radio-group v-model:value="roiInput.zon_orientatie" button-style="solid">
        <a-radio-button value="zuid">Zuid</a-radio-button>
        <a-radio-button value="oostwest">Oost/west</a-radio-button>
        <a-radio-button value="noord">Noord</a-radio-button>
       </a-radio-group>
      </a-form-item>
     </a-col>
    </template>

    <template v-if="needs.needsChargingStation">
     <a-col :xs="24" class="mb-1"><strong>Laadpaal</strong></a-col>
     <a-col :md="12" :xs="24" class="mb-2">
      <a-input v-model:value="roiInput.laadpaal_km_jaar" addon-after="km per jaar" addon-before="Elektrisch rijden"
               type="number"/>
     </a-col>
     <a-col :md="12" :xs="24" class="mb-2">
      <a-form-item label="Laadt de klant nu al thuis?">
       <a-radio-group v-model:value="roiInput.laadpaal_laadt_al_thuis" button-style="solid">
        <a-radio-button :value="false">Nee, publiek</a-radio-button>
        <a-radio-button :value="true">Ja</a-radio-button>
       </a-radio-group>
      </a-form-item>
     </a-col>
    </template>
   </a-row>
  </a-col>
 </a-row>
</template>

<script>
import {computed, ref, watch, watchEffect} from "vue";
import {useStore} from "vuex";

// Leeg = de template rekent met zijn standaardwaarde
const emptyRoiInput = () => ({
 isolatie_spouw_m2: null,
 isolatie_vloer_m2: null,
 isolatie_bodem_m2: null,
 isolatie_dak_m2: null,
 wp_afgiftesysteem: null,
 wp_ketelprijs: null,
 zon_kwp: null,
 zon_orientatie: null,
 laadpaal_km_jaar: null,
 laadpaal_laadt_al_thuis: null,
 gasverbruik_m3: null,
 leveringstarief: null,
 terugleververgoeding: null,
});

const ISOLATION_FIELDS = ['isolatie_spouw_m2', 'isolatie_vloer_m2', 'isolatie_bodem_m2', 'isolatie_dak_m2'];

const RoiInputs = {
 name: 'RoiInputs',
 props: {
  // Uitkomst van savingsInputs(): welke producten in het mandje zitten
  needs: {type: Object, required: true},
 },
 setup(props) {
  const {state} = useStore();
  const isolationForm = ref();
  const order = computed(() => state.shop.order);

  const roiInput = computed(() => {
   if (!order.value?.extraData) return null;
   if (!order.value.extraData.roiInput) {
    order.value.extraData.roiInput = emptyRoiInput();
   }
   return order.value.extraData.roiInput;
  });

  // Gasverbruik en tarieven worden al in de flow gevraagd; die niet dubbel laten invullen
  watchEffect(() => {
   const x = order.value?.extraData;
   if (!x || !roiInput.value) return;
   roiInput.value.gasverbruik_m3 = x.usage?.gas || null;
   roiInput.value.leveringstarief = x.tariff?.new?.electricity || null;
   roiInput.value.terugleververgoeding = x.tariff?.new?.returnElectricity || null;
  });

  // Zonder m² geen ISDE, geen inkoopcontrole en geen terugverdientijd voor isolatie
  const isolationModel = computed(() => ({
   totalM2: ISOLATION_FIELDS.reduce((sum, field) => sum + (+roiInput.value?.[field] > 0 ? +roiInput.value[field] : 0), 0),
  }));
  const isolationRules = {
   totalM2: [{
    validator: async (_rule, value) => {
     if (!(value > 0)) throw new Error('Vul de m² in van minimaal één isolatiemaatregel');
    },
    trigger: 'change',
   }],
  };

  // Aangeroepen door de flow voor de volgende stap
  const validated = ref(false);
  const validate = () => {
   if (!props.needs.needsIsolation || !isolationForm.value) return Promise.resolve();
   validated.value = true;
   return isolationForm.value.validate();
  };

  // De melding zit niet aan een invoerveld vast; na de eerste controle bijwerken zodra de m² veranderen
  watch(() => isolationModel.value.totalM2, () => {
   if (validated.value) isolationForm.value?.validateFields(['totalM2']).catch(() => null);
  });

  return {
   roiInput,
   isolationForm,
   isolationModel,
   isolationRules,
   validate,
  };
 },
};

export default RoiInputs;
</script>
